

import { FC, useEffect, useContext, useState, useLayoutEffect } from "react";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import { ToastTypes } from "src/types";
import { AuthContext } from "src/context/authContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import FooterText from "src/components/footerText";
import { StatisticsApiResponse, statisticsErrorAndData } from "./types";
import CustomLineChart from "src/components/manufacturer/customLineChart";
import CustomPieChart from "src/components/manufacturer/customPieChart";
import TerritoriesFilter from "src/components/manufacturer/territoriesFilter";
import { TerritoriesSearchProps } from "src/components/manufacturer/territoriesFilter/types";
import CaseDetail from "src/components/manufacturer/caseDetail";
import { CaseListingType } from "../territories/types";

const DoctorStatistics: FC = (): JSX.Element => {

  const { destroySession } = useContext(AuthContext)
  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const navigate = useNavigate();
  const params = useParams();
  let [searchParams, updateSearchParams] = useSearchParams();
  const [queryParams, changeQueryParams] = useState<TerritoriesSearchProps>({
    start_date:
      searchParams.get("start_date") ||
      format(new Date().setDate(new Date().getDate() - 60), "yyyy-MM-dd"),
    end_date:
      searchParams.get("end_date") ||
      format(new Date().setDate(new Date().getDate() + 1), "yyyy-MM-dd"),
    period: searchParams.get('period') || "day",
    filter: searchParams.get("filter") || "",
    filter_type: searchParams.get("filter_type") || "",
    type: searchParams.get('type') || "doctor",
    role: searchParams.get('role') || "doctor",
    id: searchParams.get('id') ?? params?.Id ?? "0",
    case_id: searchParams.get('case_id') || "",
    search_str: searchParams.get('search_str') || "",
  });



  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => setLoader(), [])

  const { loading: statisticsLoading, data: statisticsData, error: errorAndData, mutate: getStatsData } =
    useFetch<StatisticsApiResponse>(APIEndpoints.MANUFACTURE_STATISTICS, queryParams, {}, false);

  
  useEffect(() => {
    if (errorAndData) {
     // console.log("Error And Data..........",errorAndData)
      const { error, code } = errorAndData as unknown as statisticsErrorAndData;
       hideLoader();
      showToast([error || `Can't fetch statistics right now. Please try again in a while.`], ToastTypes.ERROR);
      if (code === 401) {
        destroySession();
        navigate(`/users/sign_in`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorAndData]);

  useEffect(() => {
    if (statisticsData) {
      if (statisticsData.code === 200) {
        showToast([statisticsData.message], ToastTypes.SUCCESS);
       // console.log("statisticsData..........",statisticsData)
        hideLoader();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statisticsData]);

  const updateQueryParamsAndCallAPI = (
    updatedQueries: TerritoriesSearchProps,
    isReset: boolean = false
  ): void => {
    if (isReset) {
      updateSearchParams({
        type: searchParams.get('type') || "doctor",
        role: searchParams.get('role') || "doctor",
        id: searchParams.get('id') ?? params?.Id ?? "0",
      });
      changeQueryParams({
        start_date: format(
          new Date().setDate(new Date().getDate() - 30),
          "yyyy-MM-dd"
        ),
        end_date: format(
          new Date().setDate(new Date().getDate()),
          "yyyy-MM-dd"
        ),
        period: "",
        filter: "",
        filter_type: "",
        case_id: "",
        search_str: "",
        type: searchParams.get('type') || "doctor",
        role: searchParams.get('role') || "doctor",
        id: searchParams.get('id') ?? params?.Id ?? "0",
      });
    } else {
      const params = new URLSearchParams({ ...queryParams, ...updatedQueries });
      updateSearchParams(params.toString(), { replace: false });
      changeQueryParams({ ...queryParams, ...updatedQueries });
    }

    setLoader();
    getStatsData({}, {}, updatedQueries);
  };

  useEffect(() => {
    const updatedParams = {
      start_date: searchParams.get('start_date') || format((new Date()).setDate((new Date()).getDate() - 60), "yyyy-MM-dd"),
      end_date: searchParams.get('end_date') || format((new Date()).setDate((new Date()).getDate() + 1), "yyyy-MM-dd"),
      period: searchParams.get('period') || "day",
      filter: searchParams.get('filter') || "",
      filter_type: searchParams.get('filter_type') || "",
      type: searchParams.get('type') ?? "doctor",
      role: searchParams.get('role') ?? "doctor",
      id: searchParams.get('id') || "",
      case_id: searchParams.get('case_id') || "",
      search_str: searchParams.get('search_str') || "",
    };
    if (!statisticsLoading) {
      setLoader();
      changeQueryParams(updatedParams);
      getStatsData({}, {}, updatedParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="container-fluid auth-page mb-4 overflow-x-hidden">
      <TerritoriesFilter defaultQueryParams={queryParams} updateQueryParams={updateQueryParamsAndCallAPI} />
      <div className="mt-2">
        {statisticsData && <CustomLineChart title={"Case Volume"} LineChartObject={statisticsData.data.case_volume} />}
      </div>
      <div className="row">
        <div className="col">
          {statisticsData && statisticsData.data && statisticsData.data.top_payors && <CustomPieChart title={"Top 10 payors"} pieChartObject={statisticsData.data.top_payors} />}
        </div>
        <div className="col">
          {statisticsData && <CustomPieChart title={"Cases By Submission Mode"} pieChartObject={statisticsData.data.mos} />}
        </div>
      </div>
      <div className="row">
        <div className="col mt-4">
          <CaseDetail baseUrl="/doctor_stats" type="doctor" searchParams={{ ...queryParams, id: Number(queryParams?.id ?? "0") }} CaseDetailListType={statisticsData?.data?.case_listing as unknown as CaseListingType[]} />
        </div>

      </div>
      <FooterText />
    </div>

  );
};

export default DoctorStatistics;
